import { Navigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useCanManageAssets from '../hooks/useCanManageAssets';

interface RootState {
  user: { role: string; userId: string };
}

interface ProfileAccessGuardProps {
  /**
   * Roles allowed to view this sub-page when it belongs to ANOTHER user.
   * The owner can always view their own page regardless of this list.
   */
  othersAllowedRoles: string[];
  children: React.ReactElement;
}

/**
 * Guards sensitive profile sub-routes (`/profile/:id/...`) where `:id` may be
 * any user. Owners always pass; viewing someone else's sensitive data
 * (payroll, documents) requires one of `othersAllowedRoles`.
 *
 * Closes a direct-URL information-disclosure hole: the profile tab navigation
 * only HID links for unauthorized viewers — the routes themselves still
 * rendered another employee's salary/documents to anyone who typed the URL.
 *
 * EFFECTIVE roles, not the primary role string. `state.user.role` is a single
 * primary role, but `AssetManagement` is an ADDITIVE Identity tag stacked on top
 * of one (a Clocking employee granted the asset tag still reports role
 * 'Clocking'). Matching only the primary role locked every non-Hr asset manager
 * out of `/profile/:id/assets` even though the server answered 200 for them —
 * exactly the people the tag exists to create. Mirrors `RoleProtectedRoute`'s
 * effective-role set and `ASSETS_PAGE_ROLES` in `App.tsx` (`['Hr','AssetManagement']`),
 * which in turn mirror the server's `IsAssetManager` = tag OR Hr. Admin is
 * deliberately NOT implicit, and Operator is out of the asset model entirely.
 *
 * `isAssetManager` tracks the server's LIVE `UserManager.IsInRoleAsync` lookup
 * (not a token claim), so it flips as soon as the tag is granted/revoked and the
 * user info is refreshed — no re-authentication needed. Only the policy-gated
 * sibling endpoints need a fresh token, so the client must not gate on one here.
 */
export // CO-372 — the guard compares against an EFFECTIVE role set, not the single primary role.
// Previously a Clocking employee holding the additive AssetManagement tag reported 'Clocking' and
// was locked out of /profile/:id/assets even though the server returned 200 — defeating the point
// of CO-364/CO-366, whose whole purpose is that a tagged employee IS an asset manager.
// ⚠️ The server's IsAssetManager() is a LIVE UserManager.IsInRoleAsync DB lookup, not a token claim,
// so it flips the instant the tag is granted; only the POLICY-gated sibling endpoints need a fresh
// token. Gate on the live flag, never on a token claim. (Revoke lag is CO-373.)
const ProfileAccessGuard = ({ othersAllowedRoles, children }: ProfileAccessGuardProps) => {
  const { id } = useParams();
  const role = useSelector((state: RootState) => state.user.role);
  const userId = useSelector((state: RootState) => state.user.userId);
  const canManageAssets = useCanManageAssets();

  const isOwnProfile = !id || id === userId;
  if (isOwnProfile) return children;

  const effectiveRoles = new Set<string>([role]);
  if (canManageAssets) effectiveRoles.add('AssetManagement');

  if (!othersAllowedRoles.some(allowed => effectiveRoles.has(allowed))) {
    return <Navigate to="/" replace />;
  }

  return children;
};
